import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { BehaviorSubject, combineLatest, merge, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';

import {
  FinnairApisRequirementKeyEnum,
  FinnairBoundItem,
  FinnairCompletionStatus,
  FinnairGender,
  FinnairLocation,
  FinnairOrder,
  FinnairTravelRequirementsEligibility,
} from '@fcom/dapi/api/models';
import { ModalButtons } from '@fcom/ui-components/components/modal';
import { LoaderType, SelectOption } from '@fcom/common/interfaces';
import { finShare } from '@fcom/rx/operators/finShare';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { ButtonTheme, ButtonType } from '@fcom/ui-components/components/buttons';
import { IconPosition } from '@fcom/ui-components/components/icons';
import { unsubscribe } from '@fcom/core/utils';
import { ScrollService } from '@fcom/common/services';
import { CountryCode } from '@fcom/core/interfaces';

import { FinnairPassengerItemWithApis } from '../../../check-in/interface/check-in.interface';
import { generateCountryOptions } from '../../../../utils/check-in.utils';

const ALL_STEPS: FinnairApisRequirementKeyEnum[] = [
  FinnairApisRequirementKeyEnum.PASSPORT,
  FinnairApisRequirementKeyEnum.COUNTRY_OF_RESIDENCE,
  FinnairApisRequirementKeyEnum.VISA,
  FinnairApisRequirementKeyEnum.PERMANENT_RESIDENT_CARD,
  FinnairApisRequirementKeyEnum.PURPOSE_OF_VISIT,
  FinnairApisRequirementKeyEnum.DESTINATION_ADDRESS,
  FinnairApisRequirementKeyEnum.HOME_ADDRESS,
  FinnairApisRequirementKeyEnum.EMERGENCY_CONTACT,
];
type StepDirection = 'next' | 'previous';

interface ApisNavigation {
  currentStep: FinnairApisRequirementKeyEnum;
  direction?: StepDirection;
}

@Component({
  selector: 'fin-apis-pax',
  templateUrl: 'apis-pax.component.html',
  styleUrls: ['./apis-pax.component.scss', './../apis-form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApisPaxComponent implements OnInit, OnDestroy {
  readonly ModalButtons = ModalButtons;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconPosition = IconPosition;
  readonly LoaderType = LoaderType;
  readonly FinnairApisRequirementKeyEnum = FinnairApisRequirementKeyEnum;

  @Input() order$: Observable<FinnairOrder>;
  @Input() boundId$: Observable<string>;
  @Input() passenger$: Observable<FinnairPassengerItemWithApis>;
  @Input() ctaOpenApisFormAction$: Observable<string>;

  @Output() goToMmb: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('scrollContainer', { static: false }) scrollContainer: ElementRef;

  subviewOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  nationalities$: Observable<SelectOption[]>;
  countries$: Observable<SelectOption[]>;
  genders$: Observable<SelectOption[]>;
  step$: Observable<FinnairApisRequirementKeyEnum>;
  steps$: Observable<FinnairApisRequirementKeyEnum[]>;
  navigate$: Subject<ApisNavigation> = new Subject();
  requiredTravelRequirements$: Observable<string[]>;
  arrivalCountryCode$: Observable<string>;
  countryCodes$: Observable<CountryCode[]>;
  hideEditButtonForPax$: Observable<boolean>;

  private subscriptions = new Subscription();

  constructor(
    private languageService: LanguageService,
    private scrollService: ScrollService
  ) {}

  ngOnInit(): void {
    this.steps$ = combineLatest([this.order$, this.boundId$, this.passenger$]).pipe(
      map(([order, boundId, passenger]) => {
        const isCarrierConnect = order.eligibilities?.checkIn?.find((e) => e.id === boundId)?.isCarrierConnect;
        return ALL_STEPS.filter((step) =>
          passenger.apisCpr?.requirements.some(
            (r) =>
              r.key === step &&
              r.required &&
              (!isCarrierConnect || (isCarrierConnect && r.status !== FinnairCompletionStatus.COMPLETED))
          )
        );
      }),
      finShare()
    );

    this.hideEditButtonForPax$ = combineLatest([this.order$, this.boundId$, this.passenger$]).pipe(
      map(([order, boundId, passenger]) => {
        const isCarrierConnect = order.eligibilities?.checkIn?.find((e) => e.id === boundId)?.isCarrierConnect;
        return (
          isCarrierConnect &&
          passenger.apisCpr?.requirements.every((r) => r.required && r.status === FinnairCompletionStatus.COMPLETED)
        );
      }),
      finShare()
    );

    const navigateSteps$ = combineLatest([this.navigate$, this.steps$]);

    this.arrivalCountryCode$ = combineLatest([this.order$, this.boundId$]).pipe(
      map(([order, boundId]) => {
        const bound = order.bounds.find((b: FinnairBoundItem) => b.id === boundId);
        const arrivalCityCode = bound?.arrival.locationCode;
        const location: FinnairLocation = order.locations[arrivalCityCode];

        return location?.countryCode || '';
      })
    );

    this.step$ = merge(
      navigateSteps$.pipe(
        map(([navigate, steps]) => {
          const currentStepIndex = navigate.currentStep ? steps.findIndex((step) => step === navigate.currentStep) : 0;
          if (this.shouldGoToMainPaxPage(navigate, steps)) {
            return steps[0];
          }
          return navigate.direction === 'next' ? steps[currentStepIndex + 1] : steps[currentStepIndex - 1];
        })
      ),
      this.steps$.pipe(
        take(1),
        map((steps) => steps[0])
      )
    ).pipe(finShare());

    this.subscriptions.add(
      this.step$.subscribe(() => {
        if (this.scrollContainer) {
          this.scrollService.scrollToImmediate(this.scrollContainer);
        }
      })
    );

    this.subscriptions.add(
      navigateSteps$.pipe(filter(([navigate, steps]) => this.shouldGoToMainPaxPage(navigate, steps))).subscribe(() => {
        this.subviewOpen$.next(false);
      })
    );

    this.requiredTravelRequirements$ = this.order$.pipe(
      map((order) =>
        Object.entries(order.eligibilities.travelRequirement).map(
          ([key, _]: [string, FinnairTravelRequirementsEligibility]) => key
        )
      )
    );

    this.countryCodes$ = combineLatest([
      this.languageService.translate('countryCodes'),
      this.languageService.translate('MMB.specialCountryCodes'),
    ]).pipe(
      map(([countryCodes, specialCountryCodes]) => {
        return countryCodes.map((code) => {
          return (
            specialCountryCodes?.find((specialCountryCode) => specialCountryCode.countryCode === code.countryCode) ??
            code
          );
        });
      }),
      finShare()
    );

    this.nationalities$ = combineLatest([this.order$, this.passenger$, this.countryCodes$]).pipe(
      map(([order, passenger, codes]) => {
        const options = codes
          .map((code) => ({ value: code.countryCode, name: code.nationality ?? code.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        return generateCountryOptions(options, order, passenger);
      }),
      finShare()
    );

    this.countries$ = combineLatest([this.order$, this.passenger$, this.countryCodes$]).pipe(
      map(([order, passenger, codes]) => {
        const options = codes
          .map((code) => ({ value: code.countryCode, name: code.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        return generateCountryOptions(options, order, passenger);
      }),
      finShare()
    );

    this.genders$ = this.languageService.translate('MMB.apis.form.genders').pipe(
      map((genders) => {
        return [FinnairGender.MALE, FinnairGender.FEMALE, FinnairGender.UNSPECIFIED].map((gender) => ({
          name: genders[gender.toLowerCase()] ?? gender,
          value: gender,
        }));
      })
    );

    this.subscriptions.add(
      combineLatest([this.ctaOpenApisFormAction$, this.passenger$.pipe(map((passenger) => passenger.id))])
        .pipe(filter(([ctaActionPassengerId, passengerId]) => ctaActionPassengerId === passengerId))
        .subscribe(() => this.showApisForm())
    );
  }

  private shouldGoToMainPaxPage(navigate: ApisNavigation, steps: FinnairApisRequirementKeyEnum[]): boolean {
    const currentStepIndex = navigate.currentStep ? steps.findIndex((step) => step === navigate.currentStep) : 0;
    return (
      (navigate.direction === 'previous' && navigate.currentStep === steps[0]) ||
      (navigate.direction === 'next' && !steps[currentStepIndex + 1])
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  navigateBack(currentStep: FinnairApisRequirementKeyEnum): void {
    this.navigate$.next({ currentStep, direction: 'previous' });
  }

  nextStep(currentStep: FinnairApisRequirementKeyEnum): void {
    this.navigate$.next({ currentStep, direction: 'next' });
  }

  showApisForm(): void {
    this.subviewOpen$.next(true);
  }
}
